/**
 * Syndicate UI (M3 S2) - the browser wrapper over syndicate-core.js.
 *
 * Mirrors command.js deliberately: same stat-strip / catalog-grid / modal
 * shapes, same window-exposure pattern for onclick strings. All rules live in
 * syndicate-core.js so the simulator sees the same behaviour; this file only
 * renders and reports.
 *
 * See docs/layers/3-syndicate/design.md §2-§3.
 */

import * as config from '../constants.js';
import { gameState, saveGameState } from './gameState.js';
import { addNotification } from './notifications.js';
import { updateTabVisibility, updateUI } from './game.js';
import { generateContractors } from './contractor.js';
import { renderLayerToolbar } from './layerNav.js';
import {
    ensureSyndicateState, writsForEra, getCharterRank, getCharterEffect,
    canFoundSyndicateCore, foundSyndicateCore, purchaseCharterUpgradeCore,
    electLeaderCore, foundingLegendRequirement,
    successionTemplate, isSuccessionArmed, successionStatus, setSuccessionPaused,
    SUCCESSION_POLICY,
} from './syndicate-core.js';
import { calculateCommandMarks, promoteCharacterCore, requiredUpgradesMissing } from './command-core.js';
import { createNewCharacter } from './character.js';
import { renderLegendCard } from './legendCard.js';
import { renderBlackoutCatalog } from './blackout.js';
import { registerSubTabs, applySubTabs } from './subTabs.js';

const GOLD = '#e0a84c';
const GOLD_DIM = '#8a6526';

const ROMAN = ['I', 'II', 'III'];

/**
 * Display name(s) for the Command upgrade(s) a charter depends on.
 *
 * Prefers the live catalog, falling back to the charter's declared
 * `requiresName` — which now only matters if an id is renamed out from under
 * the catalog, since both required upgrades ship in it.
 *
 * Takes an id OR a list, because `succession-doctrine` requires two. A lock
 * line naming one of two prerequisites is worse than none: the player buys the
 * named one and the button is still disabled.
 */
function commandUpgradeName(upgradeIds, charterDef = null) {
    const ids = Array.isArray(upgradeIds) ? upgradeIds : [upgradeIds];
    const names = ids.map(id => config.commandUpgrades?.[id]?.name || charterDef?.requiresName || id);
    return names.length > 1
        ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
        : names[0];
}

function statBlock(label, valueHtml, note) {
    return `
        <div class="flex-1 min-w-[200px] rounded-lg p-3" style="background:rgba(224,168,76,0.10); border:2px solid ${GOLD_DIM};">
            <div class="text-[11px] uppercase tracking-widest text-gray-400">${label}</div>
            <div class="text-lg font-bold leading-tight" style="color:${GOLD}">${valueHtml}</div>
            <div class="text-xs text-gray-400 mt-0.5 leading-snug">${note}</div>
        </div>`;
}

/**
 * Marks banked this era PLUS whatever the active character is carrying.
 *
 * Founding folds unbanked marks in (docs decision 8), so the preview has to as
 * well - showing only `eraMarks` would under-report the Writs the player is
 * about to receive and make the fold-in look like a bug.
 */
function projectedEra() {
    const syndicate = ensureSyndicateState();
    const unbanked = gameState.character ? (calculateCommandMarks(gameState.character)?.total || 0) : 0;
    return { eraMarks: syndicate.eraMarks, unbanked, total: syndicate.eraMarks + unbanked };
}

function renderSyndicateStats() {
    const container = document.getElementById('syndicate-stats');
    if (!container) return;
    const syndicate = ensureSyndicateState();
    const legends = gameState.command?.legends?.length || 0;
    const era = projectedEra();

    const foundedNote = syndicate.founded === 0
        ? 'No Syndicate founded yet'
        : `${syndicate.totalTours} tour${syndicate.totalTours === 1 ? '' : 's'} served across all eras`;

    const eraNote = era.unbanked > 0
        ? `${era.eraMarks.toLocaleString()} banked + ${era.unbanked.toLocaleString()} on your active character`
        : `${legends} of ${foundingLegendRequirement()} Legends promoted this era`;

    container.innerHTML =
        statBlock('Syndicate Writs', (syndicate.writs || 0).toLocaleString(), 'Issued at founding; spent on charters') +
        statBlock('Foundings', String(syndicate.founded), foundedNote) +
        statBlock('This Era', `${era.total.toLocaleString()}🎖️ → ${writsForEra(era.total)}📜`, eraNote);
}

/**
 * The founding call-to-action.
 *
 * Always shows what founding is worth right now, even when blocked - a prestige
 * the player cannot yet reach should still be legible, or the requirement reads
 * as an arbitrary wall rather than a goal.
 */
function renderFounding() {
    const container = document.getElementById('syndicate-founding');
    if (!container) return;

    const eligible = canFoundSyndicateCore();
    const era = projectedEra();
    const writs = writsForEra(era.total);
    const legends = gameState.command?.legends?.length || 0;

    const blockedCopy = {
        'insufficient-legends': `Promote ${foundingLegendRequirement() - legends} more Legend${foundingLegendRequirement() - legends === 1 ? '' : 's'} to found.`,
        'raid-in-progress': 'Finish or clear the current raid first.',
        'listed-on-marketplace': 'Your active character is listed on the marketplace.',
        'operations-in-flight': 'Wait for your operations to complete — they are led by this era’s Legends.',
        'no-command-layer': 'The Command layer is not open yet.',
    };

    const progressPct = Math.min(100, Math.round((legends / foundingLegendRequirement()) * 100));

    container.innerHTML = `
        <div class="rounded-lg px-3 py-2" style="background:rgba(224,168,76,0.07); border:2px solid ${GOLD_DIM};">
            <div class="flex flex-wrap justify-between items-center gap-3">
                <div class="min-w-0">
                    <div class="font-bold" style="color:${GOLD}">📜 Progress towards next Syndicate</div>
                    <div class="founding-subtitle text-xs text-gray-300 leading-snug">
                        Ends this Command era and issues <span class="font-bold" style="color:${GOLD}">${writs}📜</span>
                        for the ${era.total.toLocaleString()}🎖️ earned.
                    </div>
                </div>
                <button onclick="openFoundModal()" ${eligible.ok ? '' : 'disabled'}
                    class="px-4 py-1.5 rounded font-bold whitespace-nowrap ${eligible.ok ? 'text-black hover:shadow-lg' : 'bg-gray-600 text-gray-800 cursor-not-allowed'}"
                    ${eligible.ok ? `style="background:${GOLD}"` : ''}>FOUND SYNDICATE</button>
            </div>
            ${eligible.ok ? '' : `
                <!-- Bar and reason share a row: stacked, this banner cost 128px of
                     a 768px viewport for two short lines of status. -->
                <div class="flex items-center gap-2 mt-1.5">
                    <div class="flex-1 h-1.5 rounded-full bg-black/40 overflow-hidden">
                        <div class="h-full" style="width:${progressPct}%; background:${GOLD}"></div>
                    </div>
                    <div class="text-xs text-gray-400 whitespace-nowrap">${blockedCopy[eligible.reason] || 'Not available right now.'}</div>
                </div>`}
        </div>`;
}

function rankPips(owned, max) {
    let pips = '';
    for (let i = 0; i < max; i++) {
        pips += `<span class="inline-block w-2.5 h-2.5 rounded-sm mr-1" style="border:1px solid ${GOLD_DIM}; ${i < owned ? `background:${GOLD};` : ''}"></span>`;
    }
    return `<span title="Rank ${owned}/${max}">${pips}</span>`;
}

function renderCharters() {
    const container = document.getElementById('syndicate-charters');
    if (!container) return;
    const syndicate = ensureSyndicateState();

    container.innerHTML = Object.entries(config.syndicateUpgrades).map(([id, def]) => {
        const rank = getCharterRank(id);
        const maxed = rank >= def.costs.length;
        const nextCost = maxed ? null : def.costs[rank];
        // A cross-layer requirement is a real blocker, not a price problem, so
        // it gets its own line rather than a silently-disabled button.
        // Same rule as the purchase itself (`requiredUpgradesMissing`), so the
        // card and the button can never disagree about whether it is buyable.
        const missingRequirement = requiredUpgradesMissing(def);
        const affordable = !maxed && !missingRequirement.length && syndicate.writs >= nextCost;
        const rankLabel = def.costs.length > 1 && !maxed ? `Rank ${ROMAN[rank] || rank + 1} — ` : '';
        const requirementName = missingRequirement.length ? commandUpgradeName(missingRequirement, def) : null;

        return `
            <div class="catalog-card bg-white/10 border-2 border-gray-700 rounded-lg p-3 flex flex-col gap-1.5">
                <div class="flex justify-between items-baseline gap-2">
                    <h4 class="font-bold text-[15px] m-0" style="color:${GOLD}">${def.icon} ${def.name}</h4>
                    ${rankPips(rank, def.costs.length)}
                </div>
                <div class="catalog-card-flavor text-xs text-gray-400 italic leading-snug" title="${def.flavor}">${def.flavor}</div>
                <div class="catalog-card-effect text-[13px] leading-snug">${def.effect}${rank > 0 && !maxed ? ` <span class="text-game-success">Rank ${ROMAN[rank - 1] || rank} owned.</span>` : ''}</div>
                ${requirementName ? `<div class="text-xs text-amber-300">🔒 Requires the ${requirementName} Command upgrade${missingRequirement.length > 1 ? 's' : ''}.</div>` : ''}
                <div class="catalog-card-footer flex justify-between items-center mt-auto pt-2">
                    ${maxed
                        ? `<span class="text-game-success text-sm font-bold">✓ Maxed</span><span></span>`
                        : `<span class="font-bold text-sm" style="color:${GOLD}">${rankLabel}${nextCost} 📜</span>
                           <button onclick="purchaseCharter('${id}')" ${affordable ? '' : 'disabled'}
                               class="px-3 py-1.5 rounded text-xs font-bold ${affordable ? 'text-black hover:shadow-lg' : 'bg-gray-600 text-gray-800 cursor-not-allowed'}"
                               ${affordable ? `style="background:${GOLD}"` : ''}>SIGN</button>`}
                </div>
            </div>`;
    }).join('');
}

/**
 * The SAME card the Command gallery uses, in Syndicate gold.
 *
 * A Leader is a Legend who won an election — showing them as a plain text box
 * beside Command's portrait cards made the layer's most permanent character read
 * as the least important thing on the page. A Leader snapshot carries fewer
 * fields than a full Legend record, and the shared card renders only what is
 * present.
 */
function leaderCard(leader, title, note) {
    return renderLegendCard(leader, { accent: GOLD, accentDim: GOLD_DIM, title, note });
}

/**
 * Succession status.
 *
 * Reads `successionStatus()` — the SAME computation the automation acts on, so
 * the panel can never tell the player something the doctrine does not believe.
 * Shown whenever the charter is owned, including while paused.
 */
function renderSuccession() {
    const container = document.getElementById('syndicate-succession');
    if (!container) return;

    if (getCharterRank('succession-doctrine') <= 0) {
        container.innerHTML = '';
        return;
    }

    const s = successionStatus();
    const paused = ensureSyndicateState().succession?.paused;
    const template = successionTemplate();
    const pct = Math.min(100, Math.round((s.marksIfPromotedNow / Math.max(1, s.floor)) * 100));

    const state = paused ? '⏸ Paused'
        : s.due ? `▶ Promoting now — ${s.reason === 'max-tour' ? 'tour cap reached' : 'past the rate peak'}`
        : s.tourRaids < SUCCESSION_POLICY.minTourRaids
            ? `Watching — ${SUCCESSION_POLICY.minTourRaids - s.tourRaids} more raid${SUCCESSION_POLICY.minTourRaids - s.tourRaids === 1 ? '' : 's'} before the rate is judged`
        : s.marksIfPromotedNow < s.floor
            ? `Holding — promotion buys nothing yet (${s.marksIfPromotedNow}/${s.floor}🎖️)`
            : `Watching the rate — ${s.raidsSincePeak}/${SUCCESSION_POLICY.peakPatienceRaids} raids past the peak`;

    container.innerHTML = `
        <div class="rounded-lg p-4" style="background:rgba(224,168,76,0.07); border:2px solid ${GOLD_DIM};">
            <div class="flex flex-wrap justify-between items-center gap-3">
                <div>
                    <div class="font-bold" style="color:${GOLD}">🕊️ Succession Doctrine</div>
                    <div class="text-sm text-gray-300 mt-1">${state}</div>
                </div>
                <button onclick="toggleSuccessionPause()"
                    class="px-3 py-1.5 rounded text-xs font-bold ${paused ? 'text-black' : 'bg-gray-600 text-white hover:bg-gray-500'}"
                    ${paused ? `style="background:${GOLD}"` : ''}>${paused ? 'RESUME' : 'PAUSE'}</button>
            </div>
            <div class="mt-3 h-2 rounded-full bg-black/40 overflow-hidden">
                <div class="h-full" style="width:${pct}%; background:${GOLD}"></div>
            </div>
            <div class="text-xs text-gray-400 mt-1">
                Tour: ${s.tourRaids} raid${s.tourRaids === 1 ? '' : 's'} ·
                ${s.marks}🎖️ on ${gameState.character?.name || 'no one'} ·
                rate ${s.avgRate.toFixed(1)}🎖️/raid (peak ${s.peakRate.toFixed(1)})
            </div>
            <div class="text-xs text-gray-400 mt-1">
                Successor: ${template ? `${template.faction}${template.derived ? ' — mirrors your current character' : ' (your template)'}` : 'none'}
            </div>
        </div>`;
}

function renderLeadership() {
    const container = document.getElementById('syndicate-leadership');
    if (!container) return;
    const syndicate = ensureSyndicateState();

    if (!syndicate.leader) {
        // Pre-founding: show who WOULD be elected. The Leader is a permanent
        // Operations anchor, so knowing the candidate is a reason to keep
        // promoting rather than a surprise at the ceremony.
        const candidate = electLeaderCore();
        container.innerHTML = candidate
            ? leaderCard(candidate, 'Leader-presumptive', 'Elected when you found — the era’s best record leads the Syndicate permanently.')
            : `<div class="text-sm text-gray-400">No Legends yet. The era's highest-marks Legend becomes your Syndicate Leader.</div>`;
        return;
    }

    container.innerHTML =
        leaderCard(syndicate.leader, 'Syndicate Leader', 'Permanent — leads operations in every era.') +
        syndicate.pastLeaders.slice().reverse()
            .map(l => leaderCard(l, 'Past Leader', '')).join('');
}

registerSubTabs('syndicate', {
    panes: {
        charters: 'syndicate-pane-charters',
        leadership: 'syndicate-pane-leadership',
        blackout: 'syndicate-pane-blackout',
    },
    scrollers: {
        charters: 'syndicate-charters',
        leadership: 'syndicate-leadership',
        blackout: 'syndicate-blackout',
    },
});

export function renderSyndicateTab() {
    renderSyndicateStats();
    renderFounding();
    renderSuccession();
    renderCharters();
    renderLeadership();
    renderBlackoutCatalog();
    // Re-measures the scroller: the founding banner and the succession panel
    // above it both change height with state, so a fixed offset would be wrong
    // the moment a charter is bought.
    applySubTabs('syndicate');
}

export function purchaseCharter(charterId) {
    const def = config.syndicateUpgrades?.[charterId];
    const result = purchaseCharterUpgradeCore(charterId);

    if (!result.success) {
        const messages = {
            'insufficient-writs': 'Not enough Writs',
            'max-rank': 'Already at maximum rank!',
            'unknown-charter': 'Unknown charter',
            'missing-requirement': `Requires the ${commandUpgradeName(result.requires, def)} Command upgrade first`,
        };
        addNotification(messages[result.reason] || 'Could not sign that charter', 'error');
        return;
    }

    const rankSuffix = def.costs.length > 1 ? ` Rank ${ROMAN[result.rank - 1] || result.rank}` : '';
    addNotification(`📜 Signed ${def.name}${rankSuffix} (-${result.cost} 📜)`, 'success');
    saveGameState();
    renderSyndicateTab();
    updateUI();
}

// ---------------------------------------------------------------------------
// Founding ceremony
// ---------------------------------------------------------------------------

export function openFoundModal() {
    const modal = document.getElementById('found-modal');
    const body = document.getElementById('found-modal-body');
    if (!modal || !body) return;

    const eligible = canFoundSyndicateCore();
    if (!eligible.ok) {
        addNotification('Cannot found a Syndicate right now', 'error');
        return;
    }

    const era = projectedEra();
    const writs = writsForEra(era.total);
    const leader = electLeaderCore();
    // Standing Orders re-grants BOTH automation upgrades, so the ledger names
    // both. This column is the confirmation for an irreversible action —
    // under-reporting what survives is the same class of error as
    // over-reporting it.
    const keepsAutomation = getCharterRank('standing-orders') > 0;
    const endowment = getCharterEffect('founders-endowment', 0);

    // Both columns, spelled out. Founding is irreversible and wipes an era of
    // work, so the ledger IS the ceremony - a confirm dialog that only said
    // "are you sure?" would be the wrong control for this decision.
    body.innerHTML = `
        <p class="mb-4">
            Your Syndicate is founded from the officers of this era. Their record becomes its charter —
            and the Command layer starts again from nothing.
        </p>

        <div class="rounded-lg p-4 mb-4" style="background:rgba(224,168,76,0.10); border:2px solid ${GOLD_DIM};">
            <div class="text-[11px] uppercase tracking-widest text-gray-400">You receive</div>
            <div class="text-2xl font-bold mt-1" style="color:${GOLD}">${writs} 📜 Writs</div>
            <div class="text-xs text-gray-400 mt-1">
                from ${era.total.toLocaleString()}🎖️ this era${era.unbanked > 0
                    ? ` — including ${era.unbanked.toLocaleString()}🎖️ still on ${gameState.character?.name}, folded in automatically`
                    : ''}
            </div>
            ${leader ? `<div class="text-sm mt-2">Syndicate Leader: <span class="font-bold" style="color:${GOLD}">${leader.name}</span> (${(leader.marksEarned || 0).toLocaleString()}🎖️)</div>` : ''}
        </div>

        <div class="grid gap-4 mb-4" style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));">
            <div class="rounded-lg p-3 border-2 border-red-900/60" style="background:rgba(239,68,68,0.07)">
                <div class="text-[11px] uppercase tracking-widest text-red-300">Wiped</div>
                <ul class="text-[13px] mt-2 space-y-1 list-disc list-inside text-gray-200">
                    <li>All Command Marks and upgrades</li>
                    <li>All ${gameState.command?.legends?.length || 0} Legends</li>
                    <li>Regional charters</li>
                    <li>Your characters, and the vault's materials</li>
                    <li>Credits, down to ${config.progressionScaling.promotionCreditLevy.floor.toLocaleString()}</li>
                </ul>
            </div>
            <div class="rounded-lg p-3 border-2 border-green-900/60" style="background:rgba(74,222,128,0.07)">
                <div class="text-[11px] uppercase tracking-widest text-green-300">Kept</div>
                <ul class="text-[13px] mt-2 space-y-1 list-disc list-inside text-gray-200">
                    <li>Every Writ and charter you hold</li>
                    <li>Unlocked artifact designs</li>
                    <li>Artifact blueprints in the vault</li>
                    <li>Your Syndicate Leader, and past leaders</li>
                    ${keepsAutomation ? '<li>Auto-Raid Protocol and Auto-Fabricator (Standing Orders)</li>' : ''}
                    ${endowment > 0 ? `<li>${endowment}🎖️ to start the next era (Founder's Endowment)</li>` : ''}
                </ul>
            </div>
        </div>

        <div class="flex justify-end gap-3">
            <button onclick="closeFoundModal()" class="px-4 py-2 rounded font-bold bg-gray-600 hover:bg-gray-500 text-white">Not yet</button>
            <button onclick="confirmFounding()" class="px-4 py-2 rounded font-bold text-black hover:shadow-lg" style="background:${GOLD}">Sign the Charter</button>
        </div>`;

    modal.classList.remove('hidden');
}

export function closeFoundModal() {
    document.getElementById('found-modal')?.classList.add('hidden');
}

export function confirmFounding() {
    const character = gameState.character;
    const multiplayerId = character?.multiplayerId;

    const result = foundSyndicateCore();
    if (!result.success) {
        addNotification('Cannot found a Syndicate right now', 'error');
        closeFoundModal();
        return;
    }

    // Same server-side handling promotion uses: the character is gone.
    if (multiplayerId && window.multiplayer?.deleteOwnCharacter) {
        window.multiplayer.deleteOwnCharacter(multiplayerId);
    }

    // Fresh outpost for the new era.
    gameState.selectedFaction = null;
    gameState.contractors = [];
    gameState.currentRaid.hiredContractors = [];
    gameState.currentRaid.reservedContractors = [];
    generateContractors();

    closeFoundModal();
    saveGameState();

    addNotification(
        `📜 Syndicate founded — ${result.leader?.name || 'your Leader'} takes the charter · +${result.writs} 📜`,
        'success',
        { sticky: true } // irreversible, once per era, and it hands off to a fresh character
    );

    // The active slot is empty: same handoff promotion performs.
    updateTabVisibility();
    updateUI();
    renderLayerToolbar();
    renderSyndicateTab();
    if (window.showCharacterScreenAfterPromotion) {
        window.showCharacterScreenAfterPromotion();
    }
}

// ---------------------------------------------------------------------------
// Succession Doctrine — promote at the peak, raise the successor
// ---------------------------------------------------------------------------

/** Successor names. Flavour only; the player can rename from the character card. */
const SUCCESSOR_NAMES = [
  'Ash', 'Vale', 'Corr', 'Wren', 'Idris', 'Sable', 'Quill', 'Mercer', 'Rook', 'Lin',
];

function successorName(tour) {
  // Deterministic from the tour number rather than random, so a successor's
  // name is reproducible from a save rather than a fresh roll on every load.
  return `${SUCCESSOR_NAMES[tour % SUCCESSOR_NAMES.length]}-${tour}`;
}

/**
 * Promote the current character and raise their replacement, hands-off.
 *
 * Called from the auto-raid tick after a raid is collected — the moment the
 * marks rate was re-evaluated AND the raid state is clear (promotion blocks on
 * raid state, which resetRaid just released).
 *
 * The successor comes from `successionTemplate()`, which falls back to the
 * outgoing character's own faction and skill ordering. An automation that stops
 * to ask the player a question is not automation, so there is always an answer.
 */
export function promoteAndSucceed(status) {
    const outgoing = gameState.character;
    if (!outgoing) return { success: false, reason: 'no-character' };

    const template = successionTemplate();
    const multiplayerId = outgoing.multiplayerId;

    const result = promoteCharacterCore();
    if (!result.success) return result;

    if (multiplayerId && window.multiplayer?.deleteOwnCharacter) {
        window.multiplayer.deleteOwnCharacter(multiplayerId);
    }

    // Fresh outpost, exactly as the manual promote path does.
    gameState.selectedFaction = template?.faction || null;
    gameState.contractors = [];
    gameState.currentRaid.hiredContractors = [];
    gameState.currentRaid.reservedContractors = [];
    generateContractors();

    const recruit = createNewCharacter({
        name: successorName(gameState.command.tour),
        faction: template?.faction || outgoing.faction,
    });

    // Spend the recruit's opening points along the template's ordering, so the
    // successor starts pointed the same way the player was playing.
    applySkillPlan(recruit, template?.skillPlan || []);

    saveGameState();

    addNotification(
        `🕊️ Succession — ${result.legend.name} promoted (${result.marks.total}🎖️, ${status?.reason === 'max-tour' ? 'tour cap' : 'rate peak'}) · ${recruit.name} takes the field`,
        'success',
        { sticky: true } // happens while the player is away, and it replaced their character
    );

    updateTabVisibility();
    updateUI();
    renderLayerToolbar();
    renderSyndicateTab();
    return { success: true, legend: result.legend, recruit };
}

/**
 * Spend a fresh recruit's skill points down the template's priority order.
 * Levels are NOT copied — the recruit earns their own; what carries is which
 * skills the player was investing in.
 */
export function toggleSuccessionPause() {
    const paused = setSuccessionPaused(!ensureSyndicateState().succession?.paused);
    saveGameState();
    renderSyndicateTab();
    addNotification(paused ? '🕊️ Succession paused' : '🕊️ Succession resumed', paused ? 'info' : 'success');
}

function applySkillPlan(character, skillPlan) {
    if (!character || !skillPlan?.length) return;
    let points = character.skillPoints || 0;
    for (const skillId of skillPlan) {
        if (points <= 0) break;
        character.skills[skillId] = (character.skills[skillId] || 0) + 1;
        points -= 1;
    }
    character.skillPoints = points;
}

// ---------------------------------------------------------------------------
// Window exposure (onclick-string pattern used across the codebase)
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
    window.purchaseCharter = purchaseCharter;
    window.openFoundModal = openFoundModal;
    window.closeFoundModal = closeFoundModal;
    window.confirmFounding = confirmFounding;
    window.toggleSuccessionPause = toggleSuccessionPause;

    document.addEventListener('DOMContentLoaded', () => {
        document.getElementById('found-modal-close')?.addEventListener('click', closeFoundModal);
    });
}
